import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FormSpy } from 'react-final-form';
import { getIn } from 'final-form';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IKnmErknmFormEventUav } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormUav';
import { checkHasErrorInCollapseBlock } from 'functions/checkHasErrorInCollapseBlock';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';

import { Button } from '../../../UIPackage/MainModule/components/Button';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getKnmFormRef } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import styles from './ErknmEventUav.module.scss';
import { getDecisionFormRef } from '../../../DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from '../../../DecisionPackage/MainModule/interfaces/IDecisionFormRef';

interface IErknmEventUavProps {
  id: string;
  name: string;
  watchOnly: boolean;
  isPm?: boolean;
  isDecision?: boolean;
}

interface IErknmEventUavStoreProps {
  knmFormRef: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
}

const ErknmEventUavWithoutStore = ({
  id,
  name,
  watchOnly,
  knmFormRef,
  decisionFormRef,
  isPm,
  isDecision,
}: IErknmEventUavProps & IErknmEventUavStoreProps) => {
  const formRef = isDecision ? decisionFormRef : knmFormRef;

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});

  const addEventUav = useCallback(() => {
    if (!watchOnly && push) {
      const addingEventUav: IKnmErknmFormEventUav = {
        guid: uuidv4(),
        name: undefined,
        type: undefined,
        listId: uuidv4(),
        isNew: true,
      };

      push(`${name}`, addingEventUav);
    }
  }, [push, watchOnly]);

  const makeRemoveUavHandler = (index: number) => () => {
    !watchOnly && remove && remove(`${name}`, index);
  };

  return (
    <FieldBlock
      className={styles.GroupMargin}
      label={getTitleFieldWithRedStar({
        label: 'Сведения об использовании беспилотных технологий',
        field: name,
        isPm,
        isDecision,
        isKnm: !isPm && !isDecision,
      })}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          id="erknmEventUavAddButton"
          disabled={watchOnly}
          onClick={addEventUav}
        />
      }
      id={id}
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          if (!name || !props.errors) return null;

          const fieldError = getIn(props.errors, name);
          const isFieldTouched = props.touched?.[name];

          if (fieldError === REQUIRED_ERROR && isFieldTouched) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name={name}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormEventUav, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <Row>
              {fields.map((uavFieldName, sIndex) => {
                const fieldValue: IKnmErknmFormEventUav = fields.value?.[sIndex];
                const baseId = `${id}[${fieldValue?.listId}]`;

                const hasError = checkHasErrorInCollapseBlock(formRef, uavFieldName);

                return (
                  <KnmCollapse
                    id={baseId}
                    key={`uav-row-${sIndex}`}
                    title={
                      <span className={'shared-break-word'}>
                        <span>Беспилотник №{sIndex + 1}:</span>
                      </span>
                    }
                    opened={fieldValue.isNew}
                    onClose={!watchOnly ? makeRemoveUavHandler(sIndex) : undefined}
                    isNew={fieldValue.isNew}
                    changeOpenedCallback={e =>
                      setKnmCollapseOpened(state => ({
                        ...state,
                        [uavFieldName]: e,
                      }))
                    }
                    error={hasError && knmCollapseOpened[uavFieldName]}
                  >
                    <>
                      <Row>
                        <FieldBlock
                          label={getTitleFieldWithRedStar({
                            label: 'Вид беспилотника',
                            field: `${uavFieldName}.type`,
                            isKnm: !isPm && !isDecision,
                            isPm,
                            isDecision,
                          })}
                          placementLabel="left"
                          id={`${baseId}.type`}
                          color="black"
                          className={styles.FieldBlock}
                        >
                          <SelectInputFieldAutoOptions
                            name={`${uavFieldName}.type`}
                            optionName={CommonCatalogsNamesEnum.UAV_TYPES}
                            placeholder="Выберите вид"
                            disabled={watchOnly}
                            size="large"
                          />
                        </FieldBlock>
                      </Row>
                      <Row>
                        <FieldBlock
                          id={`${baseId}.name`}
                          label={getTitleFieldWithRedStar({
                            label: 'Название беспилотника',
                            field: `${uavFieldName}.name`,
                            isDecision,
                            isKnm: !isPm && !isDecision,
                            isPm,
                          })}
                          color="black"
                          placementLabel="left"
                          className={styles.FieldBlock}
                        >
                          <TextInputField
                            name={`${uavFieldName}.name`}
                            placeholder="Введите название"
                            disabled={watchOnly}
                          />
                        </FieldBlock>
                      </Row>
                    </>
                  </KnmCollapse>
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmEventUavStoreProps => ({
  knmFormRef: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
});

export const ErknmEventUav = connect(mapStateToProps)(ErknmEventUavWithoutStore);
